import React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  fetchAdminStats,
  fetchAdminTimeseries,
  fetchRevenueReport,
  type RevenueReport,
  type TimeseriesPoint,
} from "../../api/admin";
import { AtlasPanel } from "../../components/AtlasPanel";
import { StatBlock } from "../../components/StatBlock";
import { Badge } from "../../components/Badge";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { formatPence } from "./subscription-ui";

// ─── Sparkline (inline SVG, no chart library) ────────────────────────────────

function Sparkline({ points, label }: { points: TimeseriesPoint[]; label: string }) {
  const width = 460;
  const height = 96;
  const pad = { top: 10, right: 8, bottom: 22, left: 8 };

  const max = Math.max(1, ...points.map((p) => p.value));
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const coords = points.map((p, i) => {
    const x = pad.left + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
    const y = pad.top + innerH - (p.value / max) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const first = points[0]?.date;
  const last = points[points.length - 1]?.date;
  const fmt = (d?: string) =>
    d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
  const total = points.reduce((sum, p) => sum + p.value, 0);

  const axisStyle: React.CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fill: "var(--text-muted)",
  };

  return (
    <div
      style={{
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
        padding: "12px 14px 8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>{label}</span>
        <span
          className="tnum"
          style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-secondary)" }}
        >
          {total.toLocaleString()} / 90d
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={`${label}: ${total} over the last 90 days`}
      >
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={pad.top + innerH}
          y2={pad.top + innerH}
          stroke="var(--border-subtle)"
          strokeWidth={1}
        />
        {coords.length > 1 && (
          <polyline
            points={coords.join(" ")}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        <text x={pad.left} y={height - 6} style={axisStyle}>{fmt(first)}</text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" style={axisStyle}>{fmt(last)}</text>
      </svg>
    </div>
  );
}

// ─── Plan mix stacked bar ─────────────────────────────────────────────────────

function PlanMixBar({ free, paying, gifted }: { free: number; paying: number; gifted: number }) {
  const total = Math.max(1, free + paying + gifted);
  const all = [
    { label: "Free", value: free, color: "var(--surface-raised)" },
    { label: "Paying Pro", value: paying, color: "var(--sky-500)" },
    { label: "Gifted Pro", value: gifted, color: "var(--spruce-500)" },
  ];
  const segments = all.filter((s) => s.value > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: "var(--radius-pill)",
          overflow: "hidden",
          background: "var(--surface-sunken)",
          gap: 2,
        }}
      >
        {segments.map((s) => (
          <div
            key={s.label}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color, minWidth: 2 }}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {all.map((s) => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, border: "1px solid var(--border-default)" }} />
            {s.label}
            <span className="tnum" style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>
              {s.value.toLocaleString()}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Revenue panel (spec 36: same ledger as the Revenue tab) ─────────────────

function RevenuePanel({ revenue, isError }: { revenue: RevenueReport | undefined; isError: boolean }) {
  const action = (
    <Link to="/admin/revenue" style={{ fontSize: 12, color: "var(--sky-300)", textDecoration: "none" }}>
      Every sale
    </Link>
  );
  if (!revenue) {
    return (
      <AtlasPanel title="Revenue" action={action}>
        {isError ? (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>Revenue unavailable.</p>
        ) : (
          <Skel height={56} />
        )}
      </AtlasPanel>
    );
  }
  const pence = (n: number) => <span className="tnum" style={{ fontFamily: "var(--font-mono)" }}>{formatPence(n)}</span>;
  return (
    <AtlasPanel title="Revenue" action={action}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
        <StatBlock size="sm" label="Total revenue" value={pence(revenue.totals.allTimePence)} sub="all time, after refunds" />
        <StatBlock size="sm" label="This month" value={pence(revenue.totals.thisMonthPence)} sub={`${formatPence(revenue.totals.last30DaysPence)} in the last 30 days`} />
        <StatBlock
          size="sm"
          label="Subscriptions per month"
          value={pence(revenue.subscriptions.monthlyValuePence)}
          sub={`${revenue.subscriptions.yearlyCount} yearly · ${revenue.subscriptions.monthlyCount} monthly`}
        />
        <StatBlock
          size="sm"
          label="Full history one-offs"
          value={pence(revenue.fullHistory.totalPence)}
          sub={`${revenue.fullHistory.soldCount} sold`}
        />
      </div>
    </AtlasPanel>
  );
}

// ─── AdminOverviewPage ────────────────────────────────────────────────────────

export default function AdminOverviewPage() {
  const { user } = useAuth();

  const statsQuery = useQuery({
    queryKey: ["admin/stats"],
    queryFn: fetchAdminStats,
    enabled: !!user?.isAdmin,
  });
  const signupsQuery = useQuery({
    queryKey: ["admin/stats/timeseries", "signups", 90],
    queryFn: () => fetchAdminTimeseries("signups", 90),
    enabled: !!user?.isAdmin,
  });
  const activitiesQuery = useQuery({
    queryKey: ["admin/stats/timeseries", "activities", 90],
    queryFn: () => fetchAdminTimeseries("activities", 90),
    enabled: !!user?.isAdmin,
  });
  const revenueQuery = useQuery({
    queryKey: ["admin/subscriptions/revenue", 12],
    queryFn: () => fetchRevenueReport(12),
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const stats = statsQuery.data?.stats;
  const revenue = revenueQuery.data?.revenue;
  const paying = revenue?.subscriptions.paying.length ?? 0;
  const gifted = revenue?.gifted.proTotal ?? 0;

  return (
    <AdminShell title="Overview">
      {/* ── Headline stat cards ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        {statsQuery.isLoading ? (
          <>
            <Skel height={96} />
            <Skel height={96} />
            <Skel height={96} />
            <Skel height={96} />
          </>
        ) : (
          <>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Total explorers" value={(stats?.totalUsers ?? 0).toLocaleString()} sub={`${stats?.verifiedUsers ?? 0} verified`} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock
                size="sm"
                label="Paying subscribers"
                value={revenue ? paying.toLocaleString() : "—"}
                sub={revenue ? `${gifted} more on gifted Pro` : "loading"}
              />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock
                size="sm"
                label="Total revenue"
                value={
                  revenue ? (
                    <span className="tnum" style={{ fontFamily: "var(--font-mono)" }}>
                      {formatPence(revenue.totals.allTimePence)}
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-faint)", fontSize: 24 }}>—</span>
                  )
                }
                sub={revenue ? `${formatPence(revenue.subscriptions.monthlyValuePence)} a month from subscriptions` : "unavailable"}
              />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="New this week" value={(stats?.newUsers7d ?? 0).toLocaleString()} sub={`${stats?.newUsers30d ?? 0} in 30 days`} />
            </AtlasPanel>
          </>
        )}
      </div>

      {/* ── Processing health ─────────────────────────────────────────── */}
      {stats && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
          }}
        >
          {stats.unprocessedActivities > 0 ? (
            <>
              <Badge variant="danger" icon={<AlertTriangle size={11} />}>Attention</Badge>
              <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                {stats.unprocessedActivities.toLocaleString()} activities stuck unprocessed for over 10 minutes
              </span>
            </>
          ) : (
            <>
              <Badge variant="success" icon={<CheckCircle2 size={11} />}>Healthy</Badge>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                Processing pipeline is clear — no stuck activities
              </span>
            </>
          )}
          <Link
            to="/admin/pipeline"
            style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textDecoration: "none", whiteSpace: "nowrap" }}
          >
            Queues, DLQs and re-runs
          </Link>
        </div>
      )}

      {/* ── Sparklines ────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
        <AtlasPanel bodyStyle={{ padding: 14 }}>
          {signupsQuery.isLoading ? (
            <Skel height={130} />
          ) : (
            <Sparkline points={signupsQuery.data?.points ?? []} label="Signups · 90 days" />
          )}
        </AtlasPanel>
        <AtlasPanel bodyStyle={{ padding: 14 }}>
          {activitiesQuery.isLoading ? (
            <Skel height={130} />
          ) : (
            <Sparkline points={activitiesQuery.data?.points ?? []} label="Activities · 90 days" />
          )}
        </AtlasPanel>
      </div>

      {/* ── Plan mix + engagement ─────────────────────────────────────── */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          <AtlasPanel title="Plan mix">
            <PlanMixBar free={Math.max(0, stats.totalUsers - paying - gifted)} paying={paying} gifted={gifted} />
            <div style={{ marginTop: 16 }}>
              <StatBlock
                size="sm"
                label="Supporters"
                value={stats.supporters.toLocaleString()}
                sub="founding supporters (lifetime)"
              />
            </div>
          </AtlasPanel>
          <AtlasPanel title="Engagement">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 16 }}>
              <StatBlock size="sm" label="Seen 7d" value={stats.activeUsers7d.toLocaleString()} sub="opened the app" />
              <StatBlock
                size="sm"
                label="Seen 30d"
                value={stats.activeUsers30d.toLocaleString()}
                sub={`web ${stats.seenByClient30d.web} · ios ${stats.seenByClient30d.ios} · android ${stats.seenByClient30d.android}`}
              />
              <StatBlock size="sm" label="Syncing only 30d" value={stats.syncingOnly30d.toLocaleString()} sub="activities landed, app not opened" />
              <StatBlock size="sm" label="Activities" value={stats.totalActivities.toLocaleString()} sub={`${stats.activities7d} this week`} />
              <StatBlock size="sm" label="Summits" value={stats.totalSummitsLogged.toLocaleString()} />
              <StatBlock size="sm" label="Unlocks 7d" value={stats.achievementsUnlocked7d.toLocaleString()} />
              <StatBlock
                size="sm"
                label="GPX / Strava"
                value={
                  <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 22 }}>
                    {stats.gpxVsStravaSplit.gpx} / {stats.gpxVsStravaSplit.strava}
                  </span>
                }
              />
            </div>
          </AtlasPanel>
        </div>
      )}

      {/* ── Revenue ───────────────────────────────────────────────────── */}
      <RevenuePanel revenue={revenue} isError={revenueQuery.isError} />

      {/* ── Reference data sanity check ───────────────────────────────── */}
      {stats && (
        <AtlasPanel title="Reference data">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 16 }}>
            <StatBlock size="sm" label="Peaks" value={stats.content.peaks.toLocaleString()} />
            <StatBlock size="sm" label="Trails" value={stats.content.trails.toLocaleString()} />
            <StatBlock size="sm" label="Landmarks" value={stats.content.landmarks.toLocaleString()} />
            <StatBlock size="sm" label="Regions" value={stats.content.regions.toLocaleString()} />
            <StatBlock size="sm" label="Collections" value={stats.content.collections.toLocaleString()} />
          </div>
        </AtlasPanel>
      )}
    </AdminShell>
  );
}
